import { afterNextRender, ChangeDetectionStrategy, Component, effect, ElementRef, Injector, input, output, viewChild } from '@angular/core';
import { ContextNodeVm } from '../../models/overseer.view-model';
import { formatAmount } from '../../../../../utils/format-amount';

export interface NodeMenuRequest {
  lineId: number;
  event: MouseEvent;
}

/**
 * One node of the DRZEWO card's tree (0098): the client-order-tree-node look — name, amount,
 * connectors — plus the node's marker at the selected stage. Dumb and recursive: the page
 * passes the tree's root, nothing here talks to a store. A click emits the node's line id
 * (0100) and a right-click the id with its event (0102); each level re-emits its children's
 * events, so the page hears every node through the root. The focused node brings itself into
 * the card's view when the focus lands on it, so a
 * part on level five of a long product is found without scrolling by hand.
 */
@Component({
  selector: 'context-tree-node',
  templateUrl: './context-tree-node.component.html',
  styleUrl: './context-tree-node.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: {
    '[style.margin-left.px]': 'depthLevel() > 0 ? 20 : 0',
  }
})
export class ContextTreeNodeComponent {
  readonly node = input.required<ContextNodeVm>();
  readonly depthLevel = input(0);

  // the tree points back at the list (0100): the clicked line's id, for the page to focus its row
  readonly nodeClicked = output<number>();

  // a right-click on the node (0102): the page opens the node's menu at the cursor, or lets the
  // native one through — its call, so the event goes up with the id, not just the point
  readonly nodeMenuRequested = output<NodeMenuRequest>();

  readonly content = viewChild<ElementRef<HTMLElement>>('content');

  // the tree is re-derived on every stage switch and report; only the focus landing here scrolls
  private wasFocused = false;

  formatAmount = formatAmount;

  constructor(private injector: Injector) {
    effect(() => {
      const isFocused = this.node().isFocused;

      if (isFocused && !this.wasFocused) {
        // the effect runs before this node's text is in the DOM, when its row still has no
        // height — scroll once the frame is rendered, or 'nearest' lands one row short
        afterNextRender(
          () => this.content()?.nativeElement.scrollIntoView({ block: 'nearest' }),
          { injector: this.injector }
        );
      }

      this.wasFocused = isFocused;
    });
  }
}
